// Buy-now vs wait decision logic

import { getCategoryScoring } from "./categoryScoring";

export type BuyVerdict = "buy_now" | "wait" | "track_price" | "buy_refurbished" | "buy_used" | "skip_generation";

export interface BuyAdvice {
  verdict: BuyVerdict;
  confidence: number; // 0-1
  reason: string;
  alternativeAction?: string;
}

interface BuyContext {
  category: string;
  currentPrice: number;
  medianPrice: number;
  historicalLow?: number;
  priceDirection: "rising" | "stable" | "falling" | "unknown";
  daysUntilRefresh?: number; // days until new model expected
  qualityScore: number;
  urgency: "high" | "medium" | "low" | "none";
  budgetFit: "under" | "at" | "over" | "unknown";
}

export function getBuyAdvice(ctx: BuyContext): BuyAdvice {
  const scoring = getCategoryScoring(ctx.category);

  // Urgent need overrides wait logic
  if (ctx.urgency === "high") {
    return {
      verdict: "buy_now",
      confidence: 0.85,
      reason: "Urgency detected. Best available option for immediate need.",
    };
  }

  // New model cycle approaching
  if (ctx.daysUntilRefresh !== undefined && ctx.daysUntilRefresh < 45 && scoring.refreshCycle === "annual") {
    return {
      verdict: "wait",
      confidence: 0.7,
      reason: `New models expected in ~${ctx.daysUntilRefresh} days. Current pricing may drop.`,
      alternativeAction: "Track this product for price changes.",
    };
  }

  // Price is falling
  if (ctx.priceDirection === "falling" && ctx.urgency !== "medium") {
    return {
      verdict: "track_price",
      confidence: 0.65,
      reason: "Price trend is downward. Tracking may save more.",
      alternativeAction: "Set a price alert for your target.",
    };
  }

  // Budget doesn't fit but refurbished could work
  if (ctx.budgetFit === "over" && scoring.durabilityExpectation !== "disposable" && scoring.defectRiskLevel !== "high") {
    return {
      verdict: "buy_refurbished",
      confidence: 0.6,
      reason: "New price exceeds budget. Certified refurbished offers similar quality at lower cost.",
    };
  }

  // Quality below threshold
  if (ctx.qualityScore < scoring.valueFloor) {
    return {
      verdict: "skip_generation",
      confidence: 0.55,
      reason: "Current options fall below quality expectations for this category. Consider waiting for improved models.",
    };
  }

  // Price near historical low
  if (ctx.historicalLow && ctx.currentPrice <= ctx.historicalLow * 1.05) {
    return {
      verdict: "buy_now",
      confidence: 0.9,
      reason: "Price is at or near historical low. Strong time to buy.",
    };
  }

  // Diminishing returns check
  const ceiling = ctx.medianPrice * scoring.premiumCeiling;
  if (ctx.currentPrice > ceiling) {
    return {
      verdict: "buy_now",
      confidence: 0.5,
      reason: "Price is above diminishing returns threshold. Consider whether premium is justified.",
      alternativeAction: "Lower-priced alternatives may offer 90% of the quality.",
    };
  }

  // Default: buy
  return {
    verdict: "buy_now",
    confidence: 0.75,
    reason: "Good balance of price, quality, and availability.",
  };
}

export function getBuyVerdictLabel(verdict: BuyVerdict): string {
  switch (verdict) {
    case "buy_now": return "Buy now";
    case "wait": return "Wait";
    case "track_price": return "Track price";
    case "buy_refurbished": return "Consider refurbished";
    case "buy_used": return "Consider used";
    case "skip_generation": return "Skip this generation";
  }
}
